import { forwardRef, type InputHTMLAttributes, type TextareaHTMLAttributes, type ReactNode } from 'react'
import { cn } from '@/lib/cn'

const FIELD_BASE =
  'w-full bg-surface-1 text-primary border border-border-default rounded-md ' +
  'placeholder:text-tertiary transition-colors ' +
  'hover:border-border-strong focus:border-accent focus:outline-none ' +
  'disabled:opacity-45 disabled:cursor-not-allowed ' +
  'aria-[invalid=true]:border-danger'

export interface InputProps extends InputHTMLAttributes<HTMLInputElement> {
  mono?: boolean
}

export const Input = forwardRef<HTMLInputElement, InputProps>(function Input(
  { className, mono, ...props },
  ref,
) {
  return (
    <input
      ref={ref}
      className={cn(FIELD_BASE, 'h-8 px-2.5 text-base', mono && 'font-mono text-sm', className)}
      {...props}
    />
  )
})

export const Textarea = forwardRef<HTMLTextAreaElement, TextareaHTMLAttributes<HTMLTextAreaElement>>(
  function Textarea({ className, ...props }, ref) {
    return (
      <textarea
        ref={ref}
        className={cn(FIELD_BASE, 'px-2.5 py-2 text-base resize-y min-h-[64px]', className)}
        {...props}
      />
    )
  },
)

export const Select = forwardRef<HTMLSelectElement, InputHTMLAttributes<HTMLSelectElement>>(
  function Select({ className, children, ...props }, ref) {
    return (
      <select ref={ref} className={cn(FIELD_BASE, 'h-8 px-2 text-base', className)} {...props}>
        {children}
      </select>
    )
  },
)

/**
 * SKILL §10: yorliq DOIM yuqorida (floating label taqiqlanadi — skanerlash qiyin).
 * Xato maydon OSTIDA.
 */
export function Field({
  label,
  hint,
  error,
  required,
  htmlFor,
  children,
  className,
}: {
  label: ReactNode
  hint?: ReactNode
  error?: ReactNode
  required?: boolean
  htmlFor?: string
  children: ReactNode
  className?: string
}) {
  return (
    <div className={cn('flex flex-col gap-1.5', className)}>
      <label htmlFor={htmlFor} className="text-sm font-medium text-secondary">
        {label}
        {required && <span className="text-danger ml-0.5">*</span>}
      </label>
      {children}
      {error ? (
        <p className="text-xs text-danger flex items-center gap-1">
          <span aria-hidden>⚠</span>
          {error}
        </p>
      ) : hint ? (
        <p className="text-xs text-tertiary">{hint}</p>
      ) : null}
    </div>
  )
}
